/**
 * useProperty Hook
 * 
 * React hook for property operations using PropertyRepository.
 * Provides loading states, error handling, and real-time updates.
 * 
 * Usage:
 * - Single property: useProperty(propertyId)
 * - Operations only: useProperty() 
 */

import { useState, useEffect, useCallback } from 'react';
import { PropertyRepository } from '../models/PropertyRepository';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../firebase';

const repo = new PropertyRepository(db, functions);

export const useProperty = (propertyId = null) => {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(!!propertyId);
  const [error, setError] = useState(null);

  // Fetch single property with real-time updates
  useEffect(() => {
    if (!propertyId) return;

    setLoading(true);
    setError(null);

    // Timeout guard: if onSnapshot never fires (stale Firestore channel), stop spinning after 15s
    const timeoutId = setTimeout(() => {
      setLoading((current) => {
        if (current) {
          console.warn('🔧 [NEW ARCH] useProperty: onSnapshot timed out for', propertyId);
          setError(new Error('Firestore connection timed out. Hard-refresh the page (Cmd+Shift+R) to fix.'));
          return false;
        }
        return current;
      });
    }, 15000);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'discovery_properties', propertyId),
      (doc) => {
        clearTimeout(timeoutId);
        if (doc.exists()) {
          setProperty({ id: doc.id, ...doc.data() });
          setError(null);
        } else {
          setProperty(null);
          setError(new Error('Property not found'));
        }
        setLoading(false);
      },
      (err) => {
        clearTimeout(timeoutId);
        console.error('Error fetching property:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [propertyId]);

  // Delete property (cascade to mailshots/scrapes via Cloud Function)
  const deleteProperty = useCallback(async () => {
    if (!propertyId) throw new Error('No property ID provided');
    
    try {
      await repo.delete(propertyId);
    } catch (err) {
      console.error('Error deleting property:', err);
      throw err;
    }
  }, [propertyId]);

  // Update property status (updates mailshot caches via Cloud Function)
  const updateStatus = useCallback(async (status) => {
    if (!propertyId) throw new Error('No property ID provided');
    
    try {
      await repo.updateStatus(propertyId, status);
    } catch (err) {
      console.error('Error updating status:', err);
      throw err;
    }
  }, [propertyId]);

  // Add property to mailshot (atomic operation via Cloud Function)
  const addToMailshot = useCallback(async (mailshotId) => {
    if (!propertyId) throw new Error('No property ID provided');
    
    try {
      await repo.addToMailshot(propertyId, mailshotId);
    } catch (err) {
      console.error('Error adding to mailshot:', err);
      throw err;
    }
  }, [propertyId]);

  // Remove property from mailshot (atomic operation via Cloud Function)
  const removeFromMailshot = useCallback(async (mailshotId) => {
    if (!propertyId) throw new Error('No property ID provided');
    
    try {
      await repo.removeFromMailshot(propertyId, mailshotId);
    } catch (err) {
      console.error('Error removing from mailshot:', err);
      throw err;
    }
  }, [propertyId]);

  // Merge properties (consolidate duplicates via Cloud Function)
  const merge = useCallback(async (targetPropertyId, sourcePropertyIds) => {
    try {
      await repo.merge(targetPropertyId, sourcePropertyIds);
    } catch (err) {
      console.error('Error merging properties:', err);
      throw err;
    }
  }, []);

  // Update property fields
  const update = useCallback(async (updates) => {
    if (!propertyId) throw new Error('No property ID provided');
    
    try {
      await repo.update(propertyId, updates);
    } catch (err) {
      console.error('Error updating property:', err);
      throw err;
    }
  }, [propertyId]);

  // Refresh property data
  const refresh = useCallback(async () => {
    if (!propertyId) return;
    
    setLoading(true);
    try {
      const refreshed = await repo.findById(propertyId);
      setProperty(refreshed);
      setError(null);
    } catch (err) {
      console.error('Error refreshing property:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  // Ban property (mark as do not contact) - CRITICAL for compliance
  const ban = useCallback(async () => {
    if (!propertyId) throw new Error('No property ID provided');
    
    try {
      await update({ doNotContact: true });
    } catch (err) {
      console.error('Error banning property:', err);
      throw err;
    }
  }, [propertyId, update]);

  // Unban property (remove do not contact flag)
  const unban = useCallback(async () => {
    if (!propertyId) throw new Error('No property ID provided');
    
    try {
      await update({ doNotContact: false });
    } catch (err) {
      console.error('Error unbanning property:', err);
      throw err;
    }
  }, [propertyId, update]);

  return {
    property,
    loading,
    error,
    refresh,
    deleteProperty,
    updateStatus,
    addToMailshot,
    removeFromMailshot,
    merge,
    update,
    ban,
    unban,
  };
};
